import { useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button } from "../../common/Button";
import { useAuth } from "../../../contexts/AuthContext";
import api from "../../../utils/api";
import { parseApiError } from "../../../utils/parseApiError";
import type { ResetPasswordFormData, ResetPasswordPhase } from "./types";

const MINIMUM_PASSWORD_LENGTH = 6;
const INVALID_RESET_TOKEN_ERROR_CODE = "INVALID_RESET_TOKEN";

const INITIAL_FORM_DATA: ResetPasswordFormData = {
  newPassword: "",
  confirmPassword: "",
};

export const ResetPasswordPage = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { clearSession } = useAuth();

  const [formData, setFormData] =
    useState<ResetPasswordFormData>(INITIAL_FORM_DATA);

  const [phase, setPhase] = useState<ResetPasswordPhase>({
    phase: "form",
    validationError: null,
  });

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [event.target.name]: event.target.value });
    setPhase({ phase: "form", validationError: null });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (formData.newPassword.length < MINIMUM_PASSWORD_LENGTH) {
      setPhase({
        phase: "form",
        validationError: t("profile.password.min_length"),
      });
      return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setPhase({
        phase: "form",
        validationError: t("profile.password.mismatch"),
      });
      return;
    }
    if (!token) {
      setPhase({ phase: "invalidLink" });
      return;
    }

    setPhase({ phase: "submitting" });
    try {
      await api.resetPassword({ token, newPassword: formData.newPassword });
      clearSession();
      toast.success(t("auth.reset.success"));
      navigate("/login");
    } catch (error) {
      const { status, errorCode } = parseApiError({ error });
      // Only the backend's dedicated code means a rejected token; anything else is transient and keeps the form.
      if (errorCode === INVALID_RESET_TOKEN_ERROR_CODE) {
        setPhase({ phase: "invalidLink" });
        return;
      }
      toast.error(
        status === 429 ? t("auth.too_many_requests") : t("auth.request_failed"),
      );
      setPhase({ phase: "form", validationError: null });
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center bg-gray-50 py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <img
          src="/monco-logo-black.png"
          alt="Monco.ai"
          className="mx-auto h-12 w-auto"
        />
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
          {t("auth.reset.title")}
        </h2>
      </div>
      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {phase.phase === "invalidLink" ? (
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                {t("auth.reset.invalid_link")}
              </div>
              <Link
                to="/forgot-password"
                className="block text-center text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                {t("auth.reset.request_new")}
              </Link>
              <Link
                to="/login"
                className="block text-center text-sm font-medium text-blue-600 hover:text-blue-500"
              >
                {t("auth.back_to_login")}
              </Link>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              {phase.phase === "form" && phase.validationError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                  {phase.validationError}
                </div>
              )}
              <div>
                <label
                  htmlFor="newPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("profile.password.new")}
                </label>
                <div className="mt-1">
                  <input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    required
                    autoComplete="new-password"
                    disabled={phase.phase === "submitting"}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.newPassword}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-gray-700"
                >
                  {t("profile.password.confirm")}
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    autoComplete="new-password"
                    disabled={phase.phase === "submitting"}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={phase.phase === "submitting"}
                className="w-full"
              >
                {phase.phase === "submitting"
                  ? t("auth.reset.submitting")
                  : t("auth.reset.submit")}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};
